import React, { useEffect, useState } from "react";
import { X, Heart, Trophy, Crown, SkipForward, Ban } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SupporterBadge } from "@/components/SupporterBadge";

const KOFI_URL = process.env.REACT_APP_KOFI_URL;
const KOFI_MEMBERSHIP_URL = process.env.REACT_APP_KOFI_MEMBERSHIP_URL || KOFI_URL;

export function usePot(open) {
  const [pot, setPot] = useState({ goal: 9.99, raised: 0, count: 0, sponsors: [] });
  useEffect(() => {
    if (open === false) return;
    api.get("/donations/pot?scope=season").then(({ data }) => setPot(data)).catch(() => {});
  }, [open]);
  return pot;
}

export default function BondPot() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const pot = usePot(open);
  const pct = Math.min(100, Math.round((pot.raised / (pot.goal || 9.99)) * 100));
  const isSupporter = !!user?.is_supporter;

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("taskscape:open-bondpot", openHandler);
    return () => window.removeEventListener("taskscape:open-bondpot", openHandler);
  }, []);

  return (
    <>
      <button data-testid="open-bond-pot" onClick={() => setOpen(true)} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2" title="Membership & donations to keep TaskScape alive">
        <Heart size={16} className="text-[#ff6b6b]" /> <span>Support Us</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-6 w-full max-w-md max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="bond-pot-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><Heart size={22} className="text-[#ff6b6b]" /> Support TaskScape</h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-bond-pot"><X size={16} /></button>
            </div>

            <p className="osrs-shadow text-[#E6A519] text-xs mb-3 leading-snug">
              Donations help cover TaskScape's <span className="text-[#FFCF3F] font-bold">servers & hosting</span> so the site stays free for everyone. Every bit helps keep it alive!
            </p>
            <div className="border-2 border-[#2f6b1c] bg-[#1a2a12] px-3 py-2 mb-4" data-testid="bond-guarantee">
              <p className="osrs-shadow text-[#7CFC7C] text-xs leading-snug">
                💚 <span className="text-[#00FF00] font-bold">Donating is 100% optional</span> and never affects the game — it just keeps the lights on. The <span className="text-[#FFCF3F] font-bold">#1 player</span> still gets their free Bond every month (on us). You never pay to play or to win.
              </p>
            </div>

            {/* Top donators board */}
            <div className="mb-3" data-testid="top-donators-board">
              <div className="flex items-center justify-between mb-2">
                <h4 className="osrs-title text-lg flex items-center gap-2"><Trophy size={16} className="text-[#FFD700]" /> Top Donators</h4>
                <span className="osrs-num text-[10px] text-[#00FF00]">{pot.count || 0} donation{pot.count === 1 ? "" : "s"}</span>
              </div>
              {pot.sponsors?.length ? (
                <div className="flex flex-col gap-1" data-testid="donators-list">
                  {pot.sponsors.map((s, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-1.5 border-2 border-black ${i === 0 ? "bg-[#3a2f0a]" : "bg-[#241b10]"}`} data-testid={`donator-row-${i}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="osrs-num text-xs w-6 shrink-0 text-center">{["🥇", "🥈", "🥉"][i] || `#${i + 1}`}</span>
                        {i === 0 && <Crown size={13} className="fill-[#FFD700] text-[#FFD700] shrink-0" />}
                        <span className={`osrs-shadow text-sm truncate ${i === 0 ? "text-[#FFD700]" : "text-[#FFCF3F]"}`}>{s.name}</span>
                      </span>
                      <span className="osrs-num text-[#00FF00] text-sm shrink-0">${s.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="osrs-shadow text-[#E6A519] text-xs text-center py-4" data-testid="donators-empty">
                  No donations yet — be the first to help keep TaskScape alive!
                </p>
              )}
            </div>

            {pot.your_contribution > 0 && (
              <p className="osrs-shadow text-[#7CFC7C] text-xs mb-3" data-testid="pot-your-contribution">
                💚 You've chipped in <span className="osrs-num text-[#00FF00]">${pot.your_contribution?.toFixed(2)}</span> this month — thank you!
              </p>
            )}

            {KOFI_URL ? (
              <a
                href={KOFI_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="donate-kofi-btn"
                className="osrs-btn osrs-btn-green w-full py-3 text-sm flex items-center justify-center gap-2 mb-5"
              >
                <Heart size={16} /> Donate on Ko-fi
              </a>
            ) : (
              <p className="osrs-shadow text-[#FF9966] text-xs mb-5">Donations aren't set up yet.</p>
            )}

            {/* Supporter membership */}
            <div className="border-2 border-[#FFD700] bg-[#241b10] px-3 py-3 mb-5" data-testid="supporter-membership">
              <h4 className="osrs-title text-lg flex items-center gap-2 mb-2 text-[#FFD700]">
                <Crown size={18} className="fill-[#FFD700]" /> Become a Supporter
              </h4>
              {isSupporter ? (
                <div data-testid="supporter-active">
                  <p className="osrs-shadow text-[#7CFC7C] text-sm mb-2 flex items-center gap-1.5">
                    <SupporterBadge size={14} /> You're a Supporter — thank you! 💛
                  </p>
                  <p className="osrs-shadow text-[#E6A519] text-xs">
                    You have <span className="osrs-num text-[#FFCF3F]">{user?.free_skips_remaining ?? 0}</span> free task skip{(user?.free_skips_remaining ?? 0) === 1 ? "" : "s"} left this month, an ad-free board and your gold crown across chat & leaderboards.
                  </p>
                </div>
              ) : (
                <>
                  <ul className="osrs-shadow text-[#E6A519] text-xs mb-3 space-y-1.5">
                    <li className="flex items-center gap-2"><SupporterBadge size={12} /> A gold Supporter crown in chat & leaderboards</li>
                    <li className="flex items-center gap-2"><Ban size={13} className="text-[#7CFC7C]" /> An ad-free task board</li>
                    <li className="flex items-center gap-2"><SkipForward size={13} className="text-[#7CFC7C]" /> 5 free task skips every month</li>
                  </ul>
                  <div className="border border-[#b8912f] bg-black/30 px-2 py-1.5 mb-3">
                    <p className="osrs-shadow text-[#FFCF3F] text-[11px] leading-snug" data-testid="supporter-email-note">
                      ⚠ Use your account email <span className="osrs-num text-[#7CFC7C] break-all">{user?.email || "(your login email)"}</span> when subscribing on Ko-fi so your perks unlock automatically.
                    </p>
                  </div>
                  {KOFI_MEMBERSHIP_URL ? (
                    <a
                      href={KOFI_MEMBERSHIP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="supporter-join-btn"
                      className="osrs-btn w-full py-2.5 text-sm flex items-center justify-center gap-2"
                      style={{ background: "linear-gradient(#5a4a10,#3a2f0a)" }}
                    >
                      <Crown size={15} className="fill-[#FFD700] text-[#FFD700]" /> Join on Ko-fi
                    </a>
                  ) : (
                    <p className="osrs-shadow text-[#FF9966] text-xs">Memberships aren't set up yet.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
